import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import type { AppBadgeColor } from '@shared/ui/badges'
import type { LessonConnectionLog } from './interfaces'

import { DateFormat } from '@brighterly/lib-core-types/date/enums'

import {
  faSignalBarsFair,
  faSignalBarsGood,
  faSignalBarsSlash,
  faSignalBarsStrong,
} from '@fortawesome/pro-regular-svg-icons'

import { useDayjs } from '@shared/lib/format'

import { LessonConnectionParticipant, LessonConnectionTier } from './enums'

export interface LessonConnectionLogEntry extends LessonConnectionLog {
  /** How long this tier lasted until the same participant's next change; null for the latest one. */
  heldFor: string | null
}

export interface LessonConnectionSummary {
  participant: LessonConnectionParticipant
  dropCount: number
  worstTier: LessonConnectionTier
}

interface LessonConnectionTierLabel {
  text: string
  color: AppBadgeColor
  icon: IconDefinition
}

const dayjs = useDayjs()

const SECONDS_PER_MINUTE = 60
const SECONDS_PER_HOUR = 3600

const TIER_SEVERITY: Record<LessonConnectionTier, number> = {
  [LessonConnectionTier.Good]: 0,
  [LessonConnectionTier.Unstable]: 1,
  [LessonConnectionTier.Weak]: 2,
  [LessonConnectionTier.Lost]: 3,
}

const PARTICIPANT_ORDER: readonly LessonConnectionParticipant[] = [
  LessonConnectionParticipant.Customer,
  LessonConnectionParticipant.Tutor,
]

export const LESSON_PARTICIPANT_TITLES: Record<LessonConnectionParticipant, string> = {
  [LessonConnectionParticipant.Customer]: 'Customer',
  [LessonConnectionParticipant.Tutor]: 'Tutor',
}

export const LESSON_TIER_LABELS: Record<LessonConnectionTier, LessonConnectionTierLabel> = {
  [LessonConnectionTier.Good]: { text: 'Good', color: 'emerald', icon: faSignalBarsStrong },
  [LessonConnectionTier.Unstable]: { text: 'Unstable', color: 'amber', icon: faSignalBarsGood },
  [LessonConnectionTier.Weak]: { text: 'Weak', color: 'orange', icon: faSignalBarsFair },
  [LessonConnectionTier.Lost]: { text: 'Lost', color: 'red', icon: faSignalBarsSlash },
}

export const formatHeldFor = (seconds: number): string => {
  if (seconds < SECONDS_PER_MINUTE) return `${seconds}s`

  if (seconds < SECONDS_PER_HOUR) {
    const minutes = Math.floor(seconds / SECONDS_PER_MINUTE)
    return `${minutes}m ${seconds % SECONDS_PER_MINUTE}s`
  }

  const hours = Math.floor(seconds / SECONDS_PER_HOUR)
  return `${hours}h ${Math.floor((seconds % SECONDS_PER_HOUR) / SECONDS_PER_MINUTE)}m`
}

/** The samples come newest first, so a sample's next change is the closest earlier row of the same participant. */
export const buildConnectionLogEntries = (logs: LessonConnectionLog[]): LessonConnectionLogEntry[] =>
  logs.map((log, index) => {
    const nextChange = logs.slice(0, index).reverse().find(candidate => candidate.participant_type === log.participant_type)

    return {
      ...log,
      heldFor: nextChange
        ? formatHeldFor(dayjs(nextChange.created_at, DateFormat.Default).diff(dayjs(log.created_at, DateFormat.Default), 'second'))
        : null,
    }
  })

export const buildConnectionSummaries = (logs: LessonConnectionLog[]): LessonConnectionSummary[] =>
  PARTICIPANT_ORDER
    .filter(participant => logs.some(log => log.participant_type === participant))
    .map((participant) => {
      const participantLogs = logs.filter(log => log.participant_type === participant)

      return {
        participant,
        dropCount: participantLogs.filter(log => log.tier !== LessonConnectionTier.Good).length,
        worstTier: participantLogs.reduce<LessonConnectionTier>(
          (worst, log) => (TIER_SEVERITY[log.tier] > TIER_SEVERITY[worst] ? log.tier : worst),
          LessonConnectionTier.Good,
        ),
      }
    })

export const getConnectionSummaryText = (summary: LessonConnectionSummary): string => {
  const who = LESSON_PARTICIPANT_TITLES[summary.participant]

  if (summary.worstTier === LessonConnectionTier.Good) return `${who} — stayed stable`

  const drops = summary.dropCount === 1 ? '1 drop' : `${summary.dropCount} drops`

  return `${who} — ${drops}, worst:`
}

export const formatConnectionLoss = (value: number | null): string => (value === null ? '—' : `${Number(value).toFixed(1)}%`)
